import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import React, { FC, useRef, useEffect, useState, memo, useMemo } from 'react';
import * as eCharts from 'echarts';
import { useIntl, getLocale } from 'umi';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import usePieChartsHoverEffect from '@intelligence/components/Chart/hooks/usePieChartsHoverEffect';
import { getPieChartLabelBaseRich } from '@intelligence/utils/echartsUtils';
import { PIE_CHART_TOOLTIP_BASE_OPTION } from '@intelligence/utils/echartsConst';
import ChartBubble from '@/pages/intelligence/components/ChartBubble';
import { lineTooltipFormatter } from '@intelligence/pages/MobileGameBenchmark/components/RevenueTrends/linechart';
interface RegionalPieChartProps {
  pieData?: any[];
  pieOption?: any;
  lineTooltipFormatter?: any;
  metricText?: string;
  startDate?: string;
  endDate?: string;
  topChartsType?: string;
  byDevice?: boolean;
  isOnlyPrecent?: boolean;
  ifHasOtherHeadContent?: boolean;
}
const color = [
  '#376AE7',
  '#3ECBFF',
  '#4FCB87',
  '#FFD14E',
  '#ED9142',
  '#E95F6A',
  '#CF61AC',
  '#8164CD',
  '#6585F7',
  '#A0B6FA',
  '#f19e55',
  '#69d19c',
];
const RegionalPieChart: FC<RegionalPieChartProps> = (props: any) => {
  // console.log('PieChartHoverPieChartHover', props);
  let myChart: any = null;
  const {
    pieData = [],
    levelValue,
    pieOption,
    metricText = '',
    // lineTooltipFormatter,
    topChartsType = '',
    startDate = '',
    endDate = '',
    byDevice = false,
    isOnlyPrecent = false,
    secondTitleRight,
    ifHasOtherHeadContent = false,
  } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const chartRef = useRef<any>(null);
  const [options, setOptions] = useState<any>();
  const [isCenter, setIsCenter] = useState(false);
  const pieDataList = useMemo(() => {
    return pieData?.map((item: any, index: any) => {
      return {
        ...item,
        name: locale === 'zh-CN' ? item?.name_zh : item?.name,
        seriesName: locale === 'zh-CN' ? item?.name_zh : item?.name,
        color: color[index],
        itemStyle: {
          color: item?.color || color[index],
        },
        precentValue: isOnlyPrecent ? (item.value * 100).toFixed(2) : '',
      };
    });
  }, [pieData, locale]);
  const pieChartInstance = useRef<any>(null);
  const chartsInstance = useRef<any>({
    getPieInstance: () => {
      return pieChartInstance?.current;
    },
  });
  const [loading, setLoading] = useState(true);
  usePieChartsHoverEffect({
    loading,
    chartType: ChartType.Pie,
    chartsInstance,
    setOption: setOptions,
  });
  useEffect(() => {
    const gameLineOptions = {
      color,
      title: {
        text: metricText,
        left: 'center',
        top: '44%',
        textStyle: {
          fontWeight: 600,
          fontSize: 12,
          color: '#262832',
          width: 100,
          overflow: 'truncate',
        },
      },
      // graphic: {
      //   type: 'text',
      //   left: 'center',
      //   top: locale === 'zh-CN' ? '46%' : '45%',
      //   style: {
      //     text: metricText,
      //     textAlign: 'center',
      //     fill: '#262832',
      //     fontSize: 12,
      //     fontWeight: 'bold'
      //   },
      // },
      grid: {},
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        pageIconColor: '#ff781f',
        selectedMode: true,
        pageIconSize: 13,
        pageTextStyle: {
          color: '#999',
        },
        show: true,
        icon: 'circle',
        itemWidth: 20,
        itemHeight: 10,
        left: 'center',
        bottom: -4,
        textStyle: {
          // 拉近文案到左侧 legend 图标间距至 4px
          padding: [0, 0, 0, -7],
          color: '#888888',
          fontSize: 12,
        },
      },
      tooltip: {
        ...PIE_CHART_TOOLTIP_BASE_OPTION,
        show: true,
        // confine: true,
        showDelay: 10,
        hideDelay: 100,
        extraCssText: 'border: none;',
        formatter: (param: any) => {
          if (topChartsType == 'combine-stats-pie') {
            return ChartBubble({
              data: param,
              titleNow: metricText,
              topChartsType,
              startDate,
              endDate,
              chartType: 'pie',
              isCenter: isCenter && pieDataList?.length > 1,
              centerList: pieDataList,
              secondTitleRight,
              ifHasOtherHeadContent,
            });
          }
          return lineTooltipFormatter(
            isCenter ? pieDataList : [param.data],
            metricText ? metricText : formatMessage({ id: 'dbi_metric_players_by_country' }),
            '',
            (value: any, data: any, target: any) => {
              if (isOnlyPrecent) {
                if (isCenter) {
                  return (
                    `${Number(
                      target?.precentValue && target?.precentValue !== ''
                        ? target?.precentValue
                        : target?.value,
                    )?.toFixed(2)}%` || ''
                  );
                }
                return `${
                  param?.data?.precentValue && param?.data?.precentValue !== ''
                    ? param?.data?.precentValue
                    : param?.percent
                }%`;
              }
              return topChartsType == 'combine-stats-pie'
                ? newMathFormat(Number(param?.value) || '-') +
                    (param?.percent ? `(${param?.percent}%)` : '')
                : `${target?.value?.toFixed(2)}%` || '';
            },
          );
        },
        backgroundColor: 'rgba(0,0,0,0)',
      },
      series: [
        {
          data: pieDataList,
          type: 'pie',
          // radius: ['45px', '65px'],
          // radius: [50, 70],
          radius: [55, 95],
          avoidLabelOverlap: true,
          silent: false,
          startAngle: 180,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 1,
          },
          label: {
            normal: {
              padding: [0, -6],
              fontSize: 12,
              color: '#262832',
              opacity: 1,
              rich: {
                ...getPieChartLabelBaseRich(),
              },
              formatter: (params: any) => {
                const name =
                  !byDevice && topChartsType == 'combine-stats-pie'
                    ? formatMessage({ id: 'dbi_all' })
                    : params?.name;
                const percent = params?.percent === undefined ? 0 : params?.percent;
                if (params?.value && !Array.isArray(params?.value) && !isOnlyPrecent) {
                  return `${name}: ${newMathFormat(params?.value)}({per|${
                    percent?.toFixed(2) || percent
                  }%})\n{hr|}\n{a|}`;
                }
                return `${name}: {per|${
                  params?.data?.precentValue && params?.data?.precentValue !== ''
                    ? params?.data?.precentValue
                    : percent
                }%}\n{hr|}\n{a|}`;
              },
            },
          },
          labelLine: {
            // 折线弯曲程度配置
            length: 20,
            showAbove: false,
            // minTurnAngle: 80,
            maxSurfaceAngle: 80,
            lineStyle: {
              width: 2,
            },
          },
          name: 'Access From',
          minAngle: 5,
          bottom: 20,
        },
      ],
    };
    setOptions(gameLineOptions);
  }, [pieData, locale, levelValue, isCenter, metricText]);
  const handleResize = () => {
    try {
      myChart.resize();
    } catch (err) {}
  };
  useEffect(() => {
    if (myChart === null) {
      myChart = eCharts.init(chartRef!.current!);
      pieChartInstance.current = myChart;
      setLoading(false);
    }
    // console.log('optionsoptions', options);
    // console.log('pieOptionpieOption', pieOption);
    if (pieOption) {
      pieOption && myChart.setOption(pieOption);
    } else {
      options && myChart.setOption(options);
    }
  }, [options, pieOption]);
  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);
  const handlePieChartMousemove = (event: any, params) => {
    const mouseX = event.clientX;
    const mouseY = event.clientY;
    const config = chartRef.current.getBoundingClientRect();
    const pieCenterX = config.left + config.width / 2;
    const pieCenterY = config.top + config.height / 2 - 10;
    const distanceBetweenMouseAndPieCenter = Math.sqrt(
      Math.pow(pieCenterX - mouseX, 2) + Math.pow(pieCenterY - mouseY, 2),
    );
    if (distanceBetweenMouseAndPieCenter <= 50) {
      myChart?.dispatchAction({
        type: 'showTip',
        seriesIndex: 0,
        dataIndex: 0,
        position: (point: [number, number], params: any, dom: any, rect: any, size: any) => {
          const { contentSize } = size || {};
          const contentWidth = contentSize?.[0];
          return event?.target?.clientWidth - event?.offsetX < contentWidth
            ? [event?.offsetX - 20 - contentWidth, event?.offsetY + 20]
            : [event?.offsetX + 20, event?.offsetY + 20];
        },
      });
      myChart?.getZr()?.setCursorStyle('pointer');
      setIsCenter(true);
    } else {
      setIsCenter(false);
    }
  };
  useEffect(() => {
    if (chartRef.current) {
      chartRef.current?.removeEventListener('mousemove', handlePieChartMousemove, false);
      chartRef.current?.addEventListener('mousemove', handlePieChartMousemove, false);
    }
  }, [chartRef]);
  return <div ref={chartRef} style={{ height: 350, width: '100%' }}></div>;
};
RegionalPieChart.defaultProps = {
  pieData: [],
};
export default memo(RegionalPieChart);
